import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import type { Product, ProductVariant } from '../data/types';
import { logger } from '@/lib/logger';
import { getSharedSessionId } from '@/lib/session-id';
import {
  addCartItem,
  CartClaimError,
  claimCart,
  clearRemoteCart,
  fetchCart,
  removeCartItem,
  setCartItemQuantity,
  type StoreCartItem,
} from '@/features/cart/cart.api';
import { CART_REFRESH_EVENT } from '@/features/cart/cart-sync';

export type CartItem = StoreCartItem;

type CartContextValue = {
  cart: CartItem[];

  // Add item with variant information
  addItem: (product: Product, variant: ProductVariant, selectedSize?: string | null, quantity?: number) => void;

  // Quantity controls by cartKey
  increaseQty: (cartKey: string) => void;
  decreaseQty: (cartKey: string) => void;

  // Remove item
  removeItem: (cartKey: string) => void;

  // Clear all items
  clearCart: () => void;

  cartCount: number;
  cartTotal: number;

  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;

  // True until the first cart hydration resolves; isClaimingCart is true while a
  // Messenger claim link is being redeemed. Lets the UI show a loading state
  // instead of flashing an empty cart before the items arrive.
  isInitialized: boolean;
  isClaimingCart: boolean;
};

type CartStateContextValue = Pick<
  CartContextValue,
  'cart' | 'cartCount' | 'cartTotal' | 'isCartOpen' | 'isInitialized' | 'isClaimingCart'
>;
type CartActionsContextValue = Pick<
  CartContextValue,
  'addItem' | 'increaseQty' | 'decreaseQty' | 'removeItem' | 'clearCart' | 'setIsCartOpen'
>;

const CartStateContext = createContext<CartStateContextValue | undefined>(undefined);
const CartActionsContext = createContext<CartActionsContextValue | undefined>(undefined);

const makeCartKey = (productId: string, variantId: string, size: string | null) =>
  `${productId}__${variantId}__${size ?? 'none'}`;

const LEGACY_CART_KEY = 'shopping-cart';
const CART_SYNC_VERSION_KEY = 'shopping-cart-sync-v1';
const CART_CLAIM_QUERY_KEY = 'cart_claim';

function getCartClaimTokenFromUrl() {
  if (typeof window === 'undefined') return null;
  const searchToken = new URLSearchParams(window.location.search).get(CART_CLAIM_QUERY_KEY);
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const hashToken = hash ? new URLSearchParams(hash).get(CART_CLAIM_QUERY_KEY) : null;
  const token = searchToken || hashToken;
  return token?.trim() || null;
}

function clearCartClaimTokenFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : '';
  const hashParams = hash ? new URLSearchParams(hash) : null;
  const hasHashToken = Boolean(hashParams?.has(CART_CLAIM_QUERY_KEY));
  const hasSearchToken = url.searchParams.has(CART_CLAIM_QUERY_KEY);
  if (!hasSearchToken && !hasHashToken) return;

  url.searchParams.delete(CART_CLAIM_QUERY_KEY);
  if (hashParams) {
    hashParams.delete(CART_CLAIM_QUERY_KEY);
    url.hash = hashParams.toString();
  }
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Seed from the URL synchronously so the very first render already knows a
  // Messenger claim is pending and shows the loading state, not an empty cart.
  const [isClaimingCart, setIsClaimingCart] = useState(() => Boolean(getCartClaimTokenFromUrl()));
  const sessionId = useMemo(() => getSharedSessionId(), []);

  const persistCart = useCallback((nextCart: CartItem[]) => {
    localStorage.setItem(LEGACY_CART_KEY, JSON.stringify(nextCart));
    setCart(nextCart);
  }, []);

  const refreshCart = useCallback(async () => {
    try {
      const remoteCart = await fetchCart(sessionId);
      persistCart(remoteCart.items);
    } catch (error) {
      logger.error('Failed to refresh synced cart:', error);
    }
  }, [persistCart, sessionId]);

  useEffect(() => {
    let cancelled = false;

    const loadCart = async () => {
      const savedCart = localStorage.getItem(LEGACY_CART_KEY);
      let legacyCart: CartItem[] = [];

      if (savedCart) {
        try {
          legacyCart = JSON.parse(savedCart) as CartItem[];
        } catch (error) {
          logger.error('Failed to load cart from localStorage:', error);
          localStorage.removeItem(LEGACY_CART_KEY);
        }
      }

      try {
        const remoteCart = await fetchCart(sessionId);
        let nextCart = remoteCart.items;
        const needsLegacyMigration =
          legacyCart.length > 0 && localStorage.getItem(CART_SYNC_VERSION_KEY) !== 'done';

        if (needsLegacyMigration) {
          for (const item of legacyCart) {
            await addCartItem({
              sessionId,
              productId: item.productId,
              productName: item.productName,
              productSlug: item.productSlug,
              productCategory: item.productCategory,
              variantId: item.variantId,
              variantName: item.variantName,
              variantPrice: item.variantPrice,
              variantOriginalPrice: item.variantOriginalPrice,
              variantImage: item.variantImage,
              variantSku: item.variantSku,
              quantity: item.quantity,
              size: item.size,
            });
          }

          const migrated = await fetchCart(sessionId);
          nextCart = migrated.items;
          localStorage.setItem(CART_SYNC_VERSION_KEY, 'done');
        }

        const claimToken = getCartClaimTokenFromUrl();
        if (claimToken) {
          let shouldClearClaimToken = false;
          try {
            const claimed = await claimCart(claimToken, sessionId);
            shouldClearClaimToken = true;
            nextCart = claimed.items;
            if (claimed.claimStatus === 'already_claimed') {
              toast.message('Messenger сагс аль хэдийн холбогдсон байна');
            } else if (claimed.claimedItemCount > 0) {
              toast.success('Messenger сагс холбогдлоо');
            } else {
              toast.message('Messenger сагс хоосон байна');
            }
          } catch (error) {
            logger.error('Failed to claim Messenger cart:', error);
            if (error instanceof CartClaimError) {
              shouldClearClaimToken = [
                'expired',
                'used',
                'invalid',
                'missing_target',
              ].includes(error.claimStatus ?? '');

              if (error.claimStatus === 'expired') {
                toast.error('Messenger сагсны холбоосын хугацаа дууссан байна');
              } else if (error.claimStatus === 'used') {
                toast.error('Messenger сагсны холбоос аль хэдийн ашиглагдсан байна');
              } else {
                toast.error('Messenger сагсны холбоос буруу байна');
              }
            } else {
              toast.error('Messenger сагс холбож чадсангүй');
            }
          } finally {
            if (shouldClearClaimToken) {
              clearCartClaimTokenFromUrl();
            }
          }
        }

        if (!cancelled) {
          persistCart(nextCart);
        }
      } catch (error) {
        logger.error('Failed to hydrate cart from backend:', error);
        if (!cancelled) {
          setCart(legacyCart);
        }
      } finally {
        if (!cancelled) {
          setIsInitialized(true);
          setIsClaimingCart(false);
        }
      }
    };

    void loadCart();

    return () => {
      cancelled = true;
    };
  }, [persistCart, sessionId]);

  useEffect(() => {
    const handleCartRefresh = () => {
      void refreshCart();
    };

    window.addEventListener(CART_REFRESH_EVENT, handleCartRefresh);
    return () => {
      window.removeEventListener(CART_REFRESH_EVENT, handleCartRefresh);
    };
  }, [refreshCart]);

  // LocalStorage-д хадгалах (initialized болсны дараа л)
  useEffect(() => {
    // Initialized болтол бүү хадгала (race condition сэргийлэх)
    if (!isInitialized) return;

    localStorage.setItem(LEGACY_CART_KEY, JSON.stringify(cart));
  }, [cart, isInitialized]);

  // Add new item (or increase if exists)
  const addItem = useCallback((
    product: Product,
    variant: ProductVariant,
    selectedSize: string | null = null,
    quantity = 1,
  ) => {
    void addCartItem({
      sessionId,
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
      productCategory: product.category,
      variantId: variant.id,
      variantName: variant.name,
      variantPrice: Number(variant.price),
      variantOriginalPrice: variant.originalPrice ? Number(variant.originalPrice) : null,
      variantImage: variant.imagePath,
      variantSku: variant.sku,
      quantity,
      size: selectedSize,
    })
      .then((response) => {
        persistCart(response.items);
        const quantityLabel = quantity > 1 ? ` (${quantity}ш)` : '';
        toast.success(`${product.name} - ${variant.name}${quantityLabel} сагсанд нэмэгдлээ!`);
      })
      .catch((error) => {
        logger.error('Failed to add item to synced cart:', error);
        toast.error('Сагс шинэчлэхэд алдаа гарлаа');
      });
  }, [persistCart, sessionId]);

  // ✅ Increase qty (no “added” toast, optional: message)
  const increaseQty = useCallback((cartKey: string) => {
    const item = cart.find((entry) => entry.cartKey === cartKey);
    if (!item) return;

    void setCartItemQuantity(sessionId, item.productSlug, item.quantity + 1, item.size)
      .then((response) => {
        persistCart(response.items);
      })
      .catch((error) => {
        logger.error('Failed to increase cart quantity:', error);
        toast.error('Сагсны тоо шинэчлэхэд алдаа гарлаа');
      });
  }, [cart, persistCart, sessionId]);

  // Decrease qty (if becomes 0 -> remove)
  const decreaseQty = useCallback((cartKey: string) => {
    const item = cart.find((entry) => entry.cartKey === cartKey);
    if (!item) return;

    const nextQuantity = item.quantity - 1;
    void setCartItemQuantity(sessionId, item.productSlug, nextQuantity, item.size)
      .then((response) => {
        persistCart(response.items);
        if (nextQuantity <= 0) {
          toast.error(`${item.productName} - ${item.variantName} сагснаас хасагдлаа`);
        } else {
          toast.message(`${item.productName} - ${item.variantName} тоо буурлаа`);
        }
      })
      .catch((error) => {
        logger.error('Failed to decrease cart quantity:', error);
        toast.error('Сагсны тоо шинэчлэхэд алдаа гарлаа');
      });
  }, [cart, persistCart, sessionId]);


  // Remove item
  const removeItem = useCallback((cartKey: string) => {
    const item = cart.find((entry) => entry.cartKey === cartKey);
    if (!item) return;

    void removeCartItem(sessionId, item.productSlug, item.size)
      .then((response) => {
        persistCart(response.items);
        toast.error(`${item.productName} - ${item.variantName} сагснаас хасагдлаа`);
      })
      .catch((error) => {
        logger.error('Failed to remove cart item:', error);
        toast.error('Сагс шинэчлэхэд алдаа гарлаа');
      });
  }, [cart, persistCart, sessionId]);

  // Clear all items from cart
  const clearCart = useCallback(() => {
    void clearRemoteCart(sessionId)
      .then(() => {
        persistCart([]);
      })
      .catch((error) => {
        logger.error('Failed to clear synced cart:', error);
        toast.error('Сагсыг цэвэрлэхэд алдаа гарлаа');
      });
  }, [persistCart, sessionId]);

  const cartCount = useMemo(() => cart.reduce((acc, item) => acc + item.quantity, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((acc, item) => acc + item.variantPrice * item.quantity, 0), [cart]);
  const stateValue = useMemo(
    () => ({
      cart,
      cartCount,
      cartTotal,
      isCartOpen,
      isInitialized,
      isClaimingCart,
    }),
    [cart, cartCount, cartTotal, isCartOpen, isInitialized, isClaimingCart]
  );
  const actionsValue = useMemo(
    () => ({
      addItem,
      increaseQty,
      decreaseQty,
      removeItem,
      clearCart,
      setIsCartOpen,
    }),
    [addItem, increaseQty, decreaseQty, removeItem, clearCart, setIsCartOpen]
  );

  return (
    <CartStateContext.Provider value={stateValue}>
      <CartActionsContext.Provider value={actionsValue}>{children}</CartActionsContext.Provider>
    </CartStateContext.Provider>
  );
}

export const useCartState = () => {
  const context = useContext(CartStateContext);
  if (!context) throw new Error('useCartState must be used within a CartProvider');
  return context;
};

export const useCartActions = () => {
  const context = useContext(CartActionsContext);
  if (!context) throw new Error('useCartActions must be used within a CartProvider');
  return context;
};

export const useCart = () => ({
  ...useCartState(),
  ...useCartActions(),
});
